import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { LocalClientTime } from "@lib/office/stores/outlookStore";

/**
 * Helper function to convert LocalClientTime to ISO string with Outlook timezone offset
 * Format: YYYY-MM-DDTHH:mm:ss.sss±HH:MM
 */
const localClientTimeToISOString = (localTime: LocalClientTime | null): string => {
  if (!localTime) return "";
  
  try {
    // Format timezone offset from minutes to ±HH:MM
    const offsetMinutes = -localTime.timezoneOffset; // Negate because Office.js uses opposite sign
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const hours = Math.floor(absOffset / 60).toString().padStart(2, '0');
    const minutes = (absOffset % 60).toString().padStart(2, '0');
    
    // Format: YYYY-MM-DDTHH:mm:ss.sss±HH:MM
    // Note: LocalClientTime.month is 0-based (0 = January), so we need to add 1 for ISO format
    const year = localTime.year.toString().padStart(4, '0');
    const month = (localTime.month + 1).toString().padStart(2, '0'); // Add 1 for ISO format
    const day = localTime.date.toString().padStart(2, '0');
    const hour = localTime.hours.toString().padStart(2, '0');
    const minute = localTime.minutes.toString().padStart(2, '0');
    const second = localTime.seconds.toString().padStart(2, '0');
    const ms = localTime.milliseconds.toString().padStart(3, '0');
    
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}${sign}${hours}:${minutes}`;
  } catch (error) {
    console.error("❌ Failed to convert LocalClientTime to ISO string:", error);
    return "";
  }
};

export interface AppointmentState {
  startDate: Date | null;
  endDate: Date | null;
  startDateTimeUtc: string;
  endDateTimeUtc: string;
  startDateTimeZone: string; // ISO string with Outlook timezone (e.g., "2025-11-11T14:30:00.000+07:00")
  endDateTimeZone: string;   // ISO string with Outlook timezone (e.g., "2025-11-11T15:30:00.000+07:00")
  timeZone: string;          // Outlook timezone name (e.g., "Asia/Ho_Chi_Minh")
  
  // Actions
  setStartDate: (startDate: Date | null) => void;
  setEndDate: (endDate: Date | null) => void;
  setStartDateTimeUtc: (startDateTimeUtc: string) => void;
  setEndDateTimeUtc: (endDateTimeUtc: string) => void;
  setStartDateTimeZone: (startDateTimeZone: string) => void;
  setEndDateTimeZone: (endDateTimeZone: string) => void;
  setTimeZone: (timeZone: string) => void;
  
  // Sync from outlookStore with LocalClientTime
  syncFromOutlook: (
    startTime: Date | null,
    endTime: Date | null,
    startDateTimeZone: LocalClientTime | null,
    endDateTimeZone: LocalClientTime | null,
    timeZone: string | null
  ) => void;
  
  // Clear all
  clearAll: () => void;
}

const initialState = {
  startDate: null,
  endDate: null,
  startDateTimeUtc: "",
  endDateTimeUtc: "",
  startDateTimeZone: "",
  endDateTimeZone: "",
  timeZone: "",
};

export const useAppointmentStore = create<AppointmentState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        setStartDate: (startDate: Date | null) => {
          // console.log("📅 AppointmentStore: Setting start date", startDate);
          set({ startDate });
        },
        
        setEndDate: (endDate: Date | null) => {
          // console.log("📅 AppointmentStore: Setting end date", endDate);
          set({ endDate });
        },
        
        setStartDateTimeUtc: (startDateTimeUtc: string) => {
          // console.log("🌍 AppointmentStore: Setting start UTC", startDateTimeUtc);
          set({ startDateTimeUtc });
        },
        
        setEndDateTimeUtc: (endDateTimeUtc: string) => {
          // console.log("🌍 AppointmentStore: Setting end UTC", endDateTimeUtc);
          set({ endDateTimeUtc });
        },
        
        setStartDateTimeZone: (startDateTimeZone: string) => {
          // console.log("🌐 AppointmentStore: Setting start datetime with zone", startDateTimeZone);
          set({ startDateTimeZone });
        },
        
        setEndDateTimeZone: (endDateTimeZone: string) => {
          // console.log("🌐 AppointmentStore: Setting end datetime with zone", endDateTimeZone);
          set({ endDateTimeZone });
        },
        
        setTimeZone: (timeZone: string) => {
          // console.log("🌐 AppointmentStore: Setting timezone", timeZone);
          set({ timeZone });
        },
        
        // Sync from outlookStore with LocalClientTime conversion
        syncFromOutlook: (
          startTime: Date | null,
          endTime: Date | null,
          startDateTimeZone: LocalClientTime | null,
          endDateTimeZone: LocalClientTime | null,
          timeZone: string | null
        ) => {
          // console.log("🔄 AppointmentStore: Syncing from Outlook", {
          //   startTime,
          //   endTime,
          //   startDateTimeZone,
          //   endDateTimeZone,
          //   timeZone
          // });
          
          // Ensure startTime and endTime are Date objects (handle string from persist/rehydrate)
          let startDateObj: Date | null = null;
          let endDateObj: Date | null = null;
          
          if (startTime) {
            startDateObj = startTime instanceof Date ? startTime : new Date(startTime);
          }
          
          if (endTime) {
            endDateObj = endTime instanceof Date ? endTime : new Date(endTime);
          }
          
          // Convert LocalClientTime to ISO strings with Outlook timezone
          const startDateTimeZoneStr = localClientTimeToISOString(startDateTimeZone);
          const endDateTimeZoneStr = localClientTimeToISOString(endDateTimeZone);
          
          // Convert Date to UTC ISO strings
          const startDateTimeUtc = startDateObj ? startDateObj.toISOString() : "";
          const endDateTimeUtc = endDateObj ? endDateObj.toISOString() : "";
          
          set({
            startDate: startDateObj,
            endDate: endDateObj,
            startDateTimeUtc,
            endDateTimeUtc,
            startDateTimeZone: startDateTimeZoneStr,
            endDateTimeZone: endDateTimeZoneStr,
            timeZone: timeZone || "",
          });
          
          // console.log("✅ AppointmentStore: Sync completed", {
          //   startDateTimeZone: startDateTimeZoneStr,
          //   endDateTimeZone: endDateTimeZoneStr,
          //   timeZone: timeZone || "",
          // });
        },
        
        clearAll: () => {
          // console.log("🧹 AppointmentStore: Clearing all");
          set(initialState);
        },
      }),
      {
        name: "outlook-addin-appointment-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          // Only persist serializable data (not Date objects to avoid serialization issues)
          startDateTimeUtc: state.startDateTimeUtc,
          endDateTimeUtc: state.endDateTimeUtc,
          startDateTimeZone: state.startDateTimeZone,
          endDateTimeZone: state.endDateTimeZone,
          timeZone: state.timeZone,
        }),
        // Rehydrate: Convert UTC ISO strings back to Date objects
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Reconstruct Date objects from persisted UTC ISO strings
            if (state.startDateTimeUtc) {
              state.startDate = new Date(state.startDateTimeUtc);
            }
            if (state.endDateTimeUtc) {
              state.endDate = new Date(state.endDateTimeUtc);
            }
            // console.log("✅ AppointmentStore: Rehydrated from storage", {
            //   startDate: state.startDate,
            //   endDate: state.endDate,
            //   timeZone: state.timeZone,
            // });
          }
        },
      }
    ),
    {
      name: "appointment-store",
    }
  )
);
